// ======================================================================
// FILE:
// src/modules/finance/transactions/manual_journal/pages/JournalEditPage.js
//
// MODULE:
// Finance & Accounts Management System
//
// FEATURE:
// Manual Journal - Edit
//
// PURPOSE:
// Edit an existing DRAFT journal.
//
// IMPORTANT:
// - Uses existing journalService
// - Uses existing JournalHeaderForm
// - Uses existing JournalLinesTable
// - Uses existing JournalTotals
// - Does not create a new backend architecture
// - Only DRAFT journals can be edited
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

// ======================================================================
// FINANCE COMPONENTS
// ======================================================================

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import JournalHeaderForm
    from "../../../components/JournalHeaderForm";

import JournalLinesTable
    from "../../../components/JournalLinesTable";

import JournalTotals
    from "../../../components/JournalTotals";

// ======================================================================
// SERVICES
// ======================================================================

import journalService
    from "../../../services/journalService";

import financeLookupService
    from "../../../services/financeLookupService";

// ======================================================================
// JOURNAL TYPE OPTIONS
// ======================================================================

const JOURNAL_TYPE_OPTIONS = [

    {
        value: "JOURNAL",
        label: "General Journal",
    },

    {
        value: "PAYMENT",
        label: "Payment",
    },

    {
        value: "RECEIPT",
        label: "Receipt",
    },

    {
        value: "SALES",
        label: "Sales",
    },

    {
        value: "PURCHASE",
        label: "Purchase",
    },

    {
        value: "OPENING",
        label: "Opening",
    },

    {
        value: "REVERSAL",
        label: "Reversal",
    },

];

// ======================================================================
// COMPONENT
// ======================================================================

export default function JournalEditPage() {

    const navigate =
        useNavigate();

    const {
        id,
    } = useParams();

    // ==================================================================
    // STATE
    // ==================================================================

    const [
        formData,
        setFormData
    ] = useState({

        company_id:
            null,

        financial_year_id:
            null,

        financial_period_id:
            null,

        journal_date:
            "",

        journal_type:
            "JOURNAL",

        currency_id:
            null,

        exchange_rate:
            "1",

        remarks:
            "",

        company_option:
            null,

        financial_year_option:
            null,

        financial_period_option:
            null,

        currency_option:
            null,

    });

    const [
        lines,
        setLines
    ] = useState([]);

    const [
        journalNo,
        setJournalNo
    ] = useState("");

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        saving,
        setSaving
    ] = useState(false);

    const [
        errorMessage,
        setErrorMessage
    ] = useState("");

    // ==================================================================
    // CURRENCY OPTIONS
    // ==================================================================
    //
    // Keep consistent with current Create Journal implementation.
    // ==================================================================

    const [
        currencyOptions,
        setCurrencyOptions
    ] = useState([]);

    const [
        currencyLoading,
        setCurrencyLoading
    ] = useState(false);

    useEffect(
        () => {

            let cancelled = false;

            const loadCurrencies =
                async () => {

                    try {

                        setCurrencyLoading(
                            true
                        );

                        const options =
                            await financeLookupService.searchCurrencies(
                                "",
                                100
                            );

                        if (
                            !cancelled
                        ) {

                            setCurrencyOptions(
                                Array.isArray(options)
                                    ? options
                                    : []
                            );

                        }

                    } catch (error) {

                        console.error(
                            "Failed to load currencies:",
                            error
                        );

                        if (
                            !cancelled
                        ) {

                            setCurrencyOptions(
                                []
                            );

                        }

                    } finally {

                        if (
                            !cancelled
                        ) {

                            setCurrencyLoading(
                                false
                            );

                        }

                    }

                };

            loadCurrencies();

            return () => {

                cancelled = true;

            };

        },
        []
    );

    // ==================================================================
    // LOAD JOURNAL
    // ==================================================================

    useEffect(() => {

        let mounted = true;

        async function loadJournal() {

            try {

                setLoading(true);

                setErrorMessage("");

                // ------------------------------------------------------
                // Load header and lines using existing services.
                // ------------------------------------------------------

                const journal =
                    await journalService.getJournal(id);

                const journalLines =
                    await journalService.listJournalLines(id);

                if (!mounted) {
                    return;
                }
                // ================================================================
                // LOAD ACCOUNT LOOKUP FOR EXISTING JOURNAL LINES
                // ================================================================

                const accountLookup =
                    journal?.company_id
                        ? await financeLookupService.searchChartOfAccounts(
                            journal.company_id,
                            "",
                            null,
                            100
                        )
                        : [];

                const accountOptions =
                    Array.isArray(accountLookup)
                        ? accountLookup
                        : [];

                // ================================================================
                // LOAD DISPLAY VALUES FOR HEADER DROPDOWNS
                // ================================================================

                const [
                    companies,
                    financialYears,
                ] = await Promise.all([

                    financeLookupService.searchCompanies(""),

                    financeLookupService.searchFinancialYears(""),

                ]);

                const financialPeriods =
                    journal?.financial_year_id
                        ? await financeLookupService.searchFinancialPeriods(
                            journal.financial_year_id,
                            ""
                        )
                        : [];

                // ------------------------------------------------------
                // Edit is allowed only for DRAFT.
                // ------------------------------------------------------

                if (
                    String(
                        journal?.status || ""
                    ).toUpperCase() !== "DRAFT"
                ) {

                    setErrorMessage(
                        "Only DRAFT journals can be edited."
                    );

                    setLoading(false);

                    return;
                }

                // ------------------------------------------------------
                // Journal number.
                // ------------------------------------------------------

                setJournalNo(
                    journal?.journal_no ||
                    ""
                );

                // ------------------------------------------------------
                // Header.
                // ------------------------------------------------------

                setFormData({

                    company_id:
                        journal?.company_id ??
                        null,

                    financial_year_id:
                        journal?.financial_year_id ??
                        null,

                    financial_period_id:
                        journal?.financial_period_id ??
                        null,

                    journal_date:
                        journal?.journal_date
                            ? String(
                                journal.journal_date
                            ).slice(
                                0,
                                10
                            )
                            : "",

                    journal_type:
                        journal?.journal_type ||
                        "JOURNAL",

                    currency_id:
                        journal?.currency_id ??
                        null,

                    exchange_rate:
                        journal?.exchange_rate ??
                        "1",

                    remarks:
                        journal?.remarks ||
                        "",

                    company_option:
                    (
                        companies || []
                    )
                        .map(company => ({
                            value: company.id,
                            label: company.company_code
                                ? `${company.company_code} - ${company.company_name}`
                                : company.company_name,
                        }))
                        .find(
                            option =>
                                Number(option.value) ===
                                Number(journal.company_id)
                        ) || null,

                financial_year_option:
                    (
                        financialYears || []
                    )
                        .map(year => ({
                            value: year.id,
                            label:
                                year.year_name ||
                                year.year_code ||
                                `Financial Year ${year.id}`,
                        }))
                        .find(
                            option =>
                                Number(option.value) ===
                                Number(journal.financial_year_id)
                        ) || null,

                financial_period_option:
                    (
                        financialPeriods || []
                    )
                        .map(period => ({
                            value: period.id,
                            label:
                                period.period_name ||
                                period.period_code ||
                                `Period ${period.id}`,
                        }))
                        .find(
                            option =>
                                Number(option.value) ===
                                Number(journal.financial_period_id)
                        ) || null,

                    currency_option:
                        (
                            journal?.currency_name ||
                            journal?.currency_code
                        )
                            ? {
                                value:
                                    journal.currency_id,

                                label:
                                    journal.currency_code
                                        ? `${journal.currency_code} - ${journal.currency_name || ""}`
                                        : journal.currency_name,
                            }
                            : null,

                });

                // ------------------------------------------------------
                // Lines.
                //
                // Convert backend line structure into the structure
                // already used by JournalLinesTable.
                // ------------------------------------------------------

                setLines(

                    (
                        Array.isArray(
                            journalLines
                        )
                            ? journalLines
                            : []
                    ).map(

                        (
                            line
                        ) => ({

                            id:
                                line.id ??
                                null,

                            line_no:
                                line.line_no ??
                                null,

                            coa_id:
                                line.account_id ??
                                line.coa_id ??
                                null,

                            account_option:
    (() => {

        const accountId =
            line.account_id ??
            line.coa_id;

        const account =
            accountOptions.find(
                item =>
                    Number(
                        item.id ??
                        item.value
                    ) ===
                    Number(accountId)
            );

        if (!account) {
            return null;
        }

        return {
            value:
                account.id ??
                account.value,

            label:
                account.account_code
                    ? `${account.account_code} - ${account.account_name || ""}`
                    : (
                        account.label ||
                        account.account_name ||
                        `Account ${account.id ?? account.value}`
                    ),
        };

    })(),

                            cost_center_id:
                                line.cost_center_id ??
                                null,

                            cost_center_option:
                                (
                                    line.cost_center_name ||
                                    line.cost_center_code
                                )
                                    ? {

                                        value:
                                            line.cost_center_id,

                                        label:
                                            line.cost_center_code
                                                ? `${line.cost_center_code} - ${line.cost_center_name || ""}`
                                                : line.cost_center_name,

                                    }
                                    : null,

                            description:
                                line.description ||
                                "",

                            debit_amount:
                                line.debit_amount ??
                                "",

                            credit_amount:
                                line.credit_amount ??
                                "",

                        })

                    )

                );

            } catch (error) {

                console.error(
                    "Failed to load journal for editing:",
                    error
                );

                if (mounted) {

                    setErrorMessage(
                        error?.response?.data?.message ||
                        error?.message ||
                        "Failed to load journal."
                    );

                }

            } finally {

                if (mounted) {
                    setLoading(false);
                }

            }

        }

        loadJournal();

        return () => {

            mounted = false;

        };

    }, [id]);

    // ==================================================================
    // HEADER FIELD CHANGE
    // ==================================================================

    const handleFieldChange = (
        fieldName,
        value,
        selectedOption = null
    ) => {

        setFormData(
            previous => {

                const updated = {

                    ...previous,

                    [fieldName]:
                        value,

                };

                // ------------------------------------------------------
                // Company.
                // ------------------------------------------------------

                if (
                    fieldName ===
                    "company_id"
                ) {

                    updated.company_option =
                        selectedOption;

                }

                // ------------------------------------------------------
                // Financial Year.
                //
                // Period belongs to Financial Year, so clear old
                // period when FY changes.
                // ------------------------------------------------------

                if (
                    fieldName ===
                    "financial_year_id"
                ) {

                    updated.financial_year_option =
                        selectedOption;

                    updated.financial_period_id =
                        null;

                    updated.financial_period_option =
                        null;

                }

                // ------------------------------------------------------
                // Financial Period.
                // ------------------------------------------------------

                if (
                    fieldName ===
                    "financial_period_id"
                ) {

                    updated.financial_period_option =
                        selectedOption;

                }

                // ------------------------------------------------------
                // Currency.
                // ------------------------------------------------------

                if (
                    fieldName ===
                    "currency_id"
                ) {

                    updated.currency_option =
                        selectedOption;

                }

                return updated;

            }
        );

        setErrorMessage(
            ""
        );

    };

    // ==================================================================
    // COMPANY LOOKUP
    // ==================================================================

    const loadCompanies = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService.searchCompanies(
                    inputValue
                );

            return (
                response || []
            ).map(

                company => ({

                    value:
                        company.id,

                    label:
                        company.company_code
                            ? `${company.company_code} - ${company.company_name}`
                            : company.company_name,

                    data:
                        company,

                })

            );

        } catch (error) {

            console.error(
                "Failed to load companies:",
                error
            );

            return [];

        }

    };

    // ==================================================================
    // FINANCIAL YEAR LOOKUP
    // ==================================================================

    const loadFinancialYears = async (
        inputValue = ""
    ) => {

        try {

            const response =
                await financeLookupService.searchFinancialYears(
                    inputValue
                );

            return (
                response || []
            ).map(

                year => ({

                    value:
                        year.id,

                    label:
                        year.year_name ||
                        year.name ||
                        `Financial Year ${year.id}`,

                    data:
                        year,

                })

            );

        } catch (error) {

            console.error(
                "Failed to load financial years:",
                error
            );

            return [];

        }

    };

    // ==================================================================
    // FINANCIAL PERIOD LOOKUP
    // ==================================================================

    const loadFinancialPeriods = async (
        inputValue = ""
    ) => {

        if (
            !formData.financial_year_id
        ) {

            return [];

        }

        try {

            const response =
                await financeLookupService.searchFinancialPeriods(
                    formData.financial_year_id,
                    inputValue
                );

            return (
                response || []
            ).map(

                period => ({

                    value:
                        period.id,

                    label:
                        period.period_name ||
                        period.period_code ||
                        `Period ${period.id}`,

                    data:
                        period,

                })

            );

        } catch (error) {

            console.error(
                "Failed to load financial periods:",
                error
            );

            return [];

        }

    };

    // ==================================================================
    // ACCOUNT LOOKUP
    // ==================================================================
    //
    // IMPORTANT:
    // The current Create Journal implementation supplied to us has the
    // live Account/Cost Center lookup logic in a newer version than the
    // earlier source snapshot.
    //
    // Do not invent a new endpoint here.
    //
    // We will plug the exact working lookup functions from Create
    // Journal into this page once confirmed.
    // ==================================================================

    const loadAccounts =
    async (
        inputValue = ""
    ) => {

        if (
            !formData.company_id
        ) {

            return [];

        }

        try {

            return await financeLookupService
                .searchChartOfAccounts(
                    formData.company_id,
                    inputValue,
                    null,
                    20
                );

        } catch (error) {

            console.error(
                "Failed to load chart of accounts:",
                error
            );

            return [];

        }

    };

    // ==================================================================
    // COST CENTER LOOKUP
    // ==================================================================

    const loadCostCenters =
    async (
        inputValue = ""
    ) => {

        if (
            !formData.company_id
        ) {

            return [];

        }

        try {

            return await financeLookupService
                .searchCostCenters(
                    formData.company_id,
                    inputValue,
                    20
                );

        } catch (error) {

            console.error(
                "Failed to load cost centers:",
                error
            );

            return [];

        }

    };

    // ==================================================================
    // VALIDATION
    // ==================================================================

    const validateJournal = () => {

        const errors = [];

        // --------------------------------------------------------------
        // HEADER
        // --------------------------------------------------------------

        if (
            !formData.company_id
        ) {

            errors.push(
                "Company is required."
            );

        }

        if (
            !formData.financial_year_id
        ) {

            errors.push(
                "Financial Year is required."
            );

        }

        if (
            !formData.financial_period_id
        ) {

            errors.push(
                "Financial Period is required."
            );

        }

        if (
            !formData.journal_date
        ) {

            errors.push(
                "Journal Date is required."
            );

        }

        if (
            !formData.journal_type
        ) {

            errors.push(
                "Journal Type is required."
            );

        }

        if (
            !formData.currency_id
        ) {

            errors.push(
                "Currency is required."
            );

        }

        if (
            !formData.exchange_rate ||
            Number(
                formData.exchange_rate
            ) <= 0
        ) {

            errors.push(
                "Exchange Rate must be greater than zero."
            );

        }

        if (
            !formData.remarks?.trim()
        ) {

            errors.push(
                "Narration is required."
            );

        }

        // --------------------------------------------------------------
        // LINES
        // --------------------------------------------------------------

        if (
            lines.length < 2
        ) {

            errors.push(
                "At least two journal lines are required."
            );

        }

        lines.forEach(
            (
                line,
                index
            ) => {

                const lineNumber =
                    index + 1;

                if (
                    !line.coa_id
                ) {

                    errors.push(
                        `Account is required for line ${lineNumber}.`
                    );

                }

                const debit =
                    Number(
                        line.debit_amount
                    ) || 0;

                const credit =
                    Number(
                        line.credit_amount
                    ) || 0;

                if (
                    debit < 0 ||
                    credit < 0
                ) {

                    errors.push(
                        `Debit/Credit cannot be negative on line ${lineNumber}.`
                    );

                }

                if (
                    debit > 0 &&
                    credit > 0
                ) {

                    errors.push(
                        `Line ${lineNumber} cannot have both Debit and Credit.`
                    );

                }

                if (
                    debit === 0 &&
                    credit === 0
                ) {

                    errors.push(
                        `Line ${lineNumber} must have either Debit or Credit.`
                    );

                }

            }
        );

        // --------------------------------------------------------------
        // TOTALS
        // --------------------------------------------------------------

        const totalDebit =
            lines.reduce(

                (
                    total,
                    line
                ) =>

                    total +
                    (
                        Number(
                            line.debit_amount
                        ) || 0
                    ),

                0

            );

        const totalCredit =
            lines.reduce(

                (
                    total,
                    line
                ) =>

                    total +
                    (
                        Number(
                            line.credit_amount
                        ) || 0
                    ),

                0

            );

        if (
            Math.abs(
                totalDebit -
                totalCredit
            ) > 0.000001
        ) {

            errors.push(
                "Total Debit must equal Total Credit."
            );

        }

        return errors;

    };

    // ==================================================================
    // BUILD HEADER PAYLOAD
    // ==================================================================

    const buildHeaderPayload =
        () => ({

            company_id:
                Number(
                    formData.company_id
                ),

            financial_year_id:
                Number(
                    formData.financial_year_id
                ),

            financial_period_id:
                Number(
                    formData.financial_period_id
                ),

            journal_date:
                formData.journal_date,

            journal_type:
                formData.journal_type,

            currency_id:
                Number(
                    formData.currency_id
                ),

            exchange_rate:
                Number(
                    formData.exchange_rate
                ),

            remarks:
                formData.remarks
                    ?.trim() ||
                "",

        });

    // ==================================================================
    // BUILD LINE PAYLOAD
    // ==================================================================

    const buildLinePayload =
        (
            line
        ) => {

            const debit =
                Number(
                    line.debit_amount
                ) || 0;

            const credit =
                Number(
                    line.credit_amount
                ) || 0;

            const amount =
                debit > 0
                    ? debit
                    : credit;

            return {

                coa_id:
                    Number(
                        line.coa_id
                    ),

                cost_center_id:
                    line.cost_center_id
                        ? Number(
                            line.cost_center_id
                        )
                        : null,

                debit_amount:
                    debit,

                credit_amount:
                    credit,

                currency_amount:
                    amount,

                description:
                    line.description
                        ?.trim() ||
                    "",

                line_reference:
                    null,

            };

        };

    // ==================================================================
    // SAVE
    // ==================================================================

    const handleSave =
        async () => {

            setErrorMessage(
                ""
            );

            const errors =
                validateJournal();

            if (
                errors.length > 0
            ) {

                setErrorMessage(
                    errors.join(
                        " "
                    )
                );

                return;

            }

            try {

                setSaving(
                    true
                );

                // ------------------------------------------------------
                // Update Header
                // ------------------------------------------------------

                await journalService.updateJournal(
                    id,
                    buildHeaderPayload()
                );

                // ------------------------------------------------------
                // Update/Create Lines
                // ------------------------------------------------------

                for (
                    const line
                    of lines
                ) {

                    if (
                        line.id
                    ) {

                        await journalService.updateJournalLine(
                            line.id,
                            buildLinePayload(
                                line
                            )
                        );

                    } else {

                        await journalService.createJournalLine(
                            id,
                            buildLinePayload(
                                line
                            )
                        );

                    }

                }

                // ------------------------------------------------------
                // Return to List
                // ------------------------------------------------------

                navigate(
                    "/finance/manual-journal"
                );

            } catch (
                error
            ) {

                console.error(
                    "Failed to update journal:",
                    error
                );

                setErrorMessage(
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to update journal."
                );

            } finally {

                setSaving(
                    false
                );

            }

        };

    // ==================================================================
    // CANCEL
    // ==================================================================

    const handleCancel =
        () => {

            if (
                saving
            ) {
                return;
            }

            navigate(
                "/finance/manual-journal"
            );

        };

    // ==================================================================
    // LOADING SCREEN
    // ==================================================================

    if (
        loading
    ) {

        return (

            <div className="container-fluid">

                <FinancePageHeader
                    icon="fas fa-edit"
                    title="Edit Journal"
                    subtitle="Loading journal..."
                    breadcrumbs={[
                        {
                            label:
                                "Finance",

                            path:
                                "/finance",
                        },

                        {
                            label:
                                "Manual Journal",

                            path:
                                "/finance/manual-journal",
                        },

                        {
                            label:
                                "Edit Journal",
                        },
                    ]}
                />

                <div className="card border-0 shadow-sm">

                    <div className="card-body text-center py-5">

                        <div
                            className="spinner-border text-primary"
                            role="status"
                        />

                        <div className="mt-3">
                            Loading journal...
                        </div>

                    </div>

                </div>

            </div>

        );

    }

    // ==================================================================
    // MAIN SCREEN
    // ==================================================================

    return (

        <div className="container-fluid">

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader
                icon="fas fa-edit"
                title="Edit Journal"
                subtitle={
                    journalNo
                        ? `Edit ${journalNo}`
                        : "Edit manual journal"
                }
                breadcrumbs={[
                    {
                        label:
                            "Finance",

                        path:
                            "/finance",
                    },

                    {
                        label:
                            "Manual Journal",

                        path:
                            "/finance/manual-journal",
                    },

                    {
                        label:
                            "Edit Journal",
                    },
                ]}
                actions={

                    <>

                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2"
                            onClick={
                                handleCancel
                            }
                            disabled={
                                saving
                            }
                        >

                            Cancel

                        </button>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={
                                handleSave
                            }
                            disabled={
                                saving
                            }
                        >

                            {saving ? (

                                <>

                                    <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                    />

                                    Saving...

                                </>

                            ) : (

                                <>

                                    <i className="fas fa-save me-1" />

                                    Save Changes

                                </>

                            )}

                        </button>

                    </>

                }
            />

            {/* ==========================================================
                ERROR
            ========================================================== */}

            {errorMessage && (

                <div className="alert alert-danger">

                    {errorMessage}

                </div>

            )}

            {/* ==========================================================
                JOURNAL HEADER
            ========================================================== */}

            <JournalHeaderForm

                formData={
                    formData
                }

                onFieldChange={
                    handleFieldChange
                }

                journalNo={
                    journalNo
                }

                journalTypeOptions={
                    JOURNAL_TYPE_OPTIONS
                }

                currencyOptions={
                    currencyOptions
                }

                selectedCompany={
                    formData.company_option
                }

                selectedFinancialYear={
                    formData.financial_year_option
                }

                selectedFinancialPeriod={
                    formData.financial_period_option
                }

                selectedCurrency={
                    formData.currency_option
                }

                disabled={
                    saving
                }

                loading={
                    saving
                }

            />

            {/* ==========================================================
                JOURNAL LINES
            ========================================================== */}

            <JournalLinesTable

                lines={
                    lines
                }

                onLinesChange={
                    setLines
                }

                loadAccounts={
                    loadAccounts
                }

                loadCostCenters={
                    loadCostCenters
                }

                disabled={
                    saving
                }

                loading={
                    saving
                }

            />

            {/* ==========================================================
                TOTALS
            ========================================================== */}

            <JournalTotals

                lines={
                    lines
                }

                narration={
                    formData.remarks
                }

            />

        </div>

    );

}